import React, { useMemo } from 'react';
import { Submission, Participant, Task } from '../types';
import { UsersIcon } from './icons';
import Avatar from './Avatar';

/**
 * Converts a timestamp value (Firestore Timestamp object, serialized {seconds, nanoseconds},
 * ISO string, or Date) into a JavaScript Date object.
 */
const toDate = (value: any): Date => {
    if (!value) return new Date(0);
    // Firestore Timestamp objects have a toDate() method
    if (typeof value?.toDate === 'function') return value.toDate();
    // Already a Date
    if (value instanceof Date) return value;
    // Serialized Firestore Timestamp (from JSON.parse/JSON.stringify): {seconds, nanoseconds}
    if (typeof value === 'object' && 'seconds' in value) {
        return new Date(value.seconds * 1000);
    }
    // ISO string or numeric timestamp
    return new Date(value);
};

const formatTimeAgo = (timestamp: any): string => {
    const date = toDate(timestamp);
    if (isNaN(date.getTime())) return '—';

    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 0) return 'Just now'; // future dates

    let interval = seconds / 31536000;
    if (interval > 1) return `${Math.floor(interval)}y ago`;

    interval = seconds / 2592000;
    if (interval > 1) return `${Math.floor(interval)}mo ago`;

    interval = seconds / 86400;
    if (interval > 1) return `${Math.floor(interval)}d ago`;

    interval = seconds / 3600;
    if (interval > 1) return `${Math.floor(interval)}h ago`;

    interval = seconds / 60;
    if (interval > 1) return `${Math.floor(interval)}m ago`;

    return 'Just now';
};

type ParticipantStatus = 'Completed' | 'In Progress' | 'Not Started';

interface ParticipantRow {
    participant: Participant;
    completedTasks: number;
    totalAssignedTasks: number;
    progress: number;
    lastActive: any; // Can be Firestore Timestamp, ISO string, or null
    status: ParticipantStatus;
}

interface ParticipantProgressTableProps {
    participants: Participant[];
    submissions: Submission[];
    tasks: Task[];
}

const StatusBadge: React.FC<{ status: ParticipantStatus }> = ({ status }) => {
    const styles = {
        'Completed': 'bg-status-ok-bg text-status-ok border-status-ok',
        'In Progress': 'bg-action/5 text-action border-action/10',
        'Not Started': 'bg-app-bg text-text-faint border-app-border',
    };

    return (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${styles[status]}`}>
            {status === 'Completed' && <span className="w-1.5 h-1.5 rounded-full bg-status-ok mr-2" />}
            {status === 'In Progress' && <span className="w-1.5 h-1.5 rounded-full bg-action mr-2" />}
            {status === 'Not Started' && <span className="w-1.5 h-1.5 rounded-full bg-neutral-300 mr-2" />}
            {status}
        </span>
    );
};

const ParticipantProgressTable: React.FC<ParticipantProgressTableProps> = ({
    participants,
    submissions,
    tasks,
}) => {
    const rows: ParticipantRow[] = useMemo(() => {
        return participants.map(participant => {
            // Count completed tasks (tasks that have a submission from this participant)
            const submissionsForParticipant = submissions.filter(s => s.participantId === participant.id);
            const completedTaskIds = new Set(submissionsForParticipant.map(s => s.taskId).filter(Boolean));

            // Find tasks assigned to this participant OR tasks they have already completed (for admins/testers)
            const assignedTasks = tasks.filter(task => {
                // 1. If user already completed it, it's relevant
                if (completedTaskIds.has(task.id)) return true;

                // 2. If no specific assignments, everyone is assigned
                if (!task.assignedParticipantIds || task.assignedParticipantIds.length === 0) {
                    return true;
                }

                // 3. Specifically assigned to this user
                return task.assignedParticipantIds.includes(participant.id);
            });

            const completedTasks = assignedTasks.filter(t => completedTaskIds.has(t.id)).length;
            const totalAssignedTasks = assignedTasks.length;

            // Progress percentage
            const progress = totalAssignedTasks > 0
                ? Math.round((completedTasks / totalAssignedTasks) * 100)
                : 0;

            // Last active (most recent submission timestamp)
            const sortedSubmissions = [...submissionsForParticipant].sort(
                (a, b) => toDate(b.timestamp).getTime() - toDate(a.timestamp).getTime()
            );
            const lastActive = sortedSubmissions.length > 0 ? sortedSubmissions[0].timestamp : null;

            // Status
            let status: ParticipantStatus;
            if (completedTasks === totalAssignedTasks && totalAssignedTasks > 0) {
                status = 'Completed';
            } else if (completedTasks > 0) {
                status = 'In Progress';
            } else {
                status = 'Not Started';
            }

            return { participant, completedTasks, totalAssignedTasks, progress, lastActive, status };
        }).sort((a, b) => a.progress - b.progress); // Lowest progress first
    }, [participants, submissions, tasks]);

    if (participants.length === 0) {
        return (
            <div className="bg-white rounded-2xl border border-app-border p-8">
                <h2 className="text-text-ink font-bold text-xl tracking-tight mb-6">Participant Progress</h2>
                <div className="flex flex-col items-center justify-center py-8 text-center text-text-muted">
                    <UsersIcon className="w-10 h-10 mb-2 opacity-50" />
                    <p className="text-sm font-medium">No participants in this project yet.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl border border-app-border p-6 sm:p-8 resize-y overflow-auto min-h-[250px]" style={{ resize: 'vertical' }}>
            <div className="flex items-baseline gap-3 mb-6">
                <h2 className="text-xl font-semibold text-text-ink">Participant Progress</h2>
                <span className="text-xs font-semibold text-text-muted uppercase tracking-wider flex-shrink-0">
                    {participants.length} participant{participants.length !== 1 ? 's' : ''}
                </span>
            </div>

            {/* Table */}
            <div className="overflow-x-auto -mx-2">
                <table className="w-full min-w-[600px]">
                    <thead>
                        <tr className="border-b border-app-border">
                            <th className="text-left text-xs font-semibold text-text-muted uppercase tracking-wider pb-3.5 px-2">
                                Participant
                            </th>
                            <th className="text-left text-xs font-semibold text-text-muted uppercase tracking-wider pb-3.5 px-2">
                                Tasks
                            </th>
                            <th className="text-left text-xs font-semibold text-text-muted uppercase tracking-wider pb-3.5 px-2 w-[200px]">
                                Progress
                            </th>
                            <th className="text-left text-xs font-semibold text-text-muted uppercase tracking-wider pb-3.5 px-2">
                                Last Active
                            </th>
                            <th className="text-left text-xs font-semibold text-text-muted uppercase tracking-wider pb-3.5 px-2">
                                Status
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr
                                key={row.participant.id}
                                className="border-b border-app-border last:border-b-0 hover:bg-app-bg transition-colors"
                            >
                                {/* Participant */}
                                <td className="py-3.5 px-2">
                                    <div className="flex items-center gap-3">
                                        <Avatar
                                            name={row.participant.name}
                                            avatarUrl={row.participant.avatarUrl}
                                            id={row.participant.id}
                                            className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                                        />
                                        <div>
                                            <p className="text-[13px] font-bold text-text-ink">{row.participant.name}</p>
                                            {row.participant.location && (
                                                <p className="text-[11px] font-medium text-text-muted opacity-80 uppercase tracking-wider">{row.participant.location}</p>
                                            )}
                                        </div>
                                    </div>
                                </td>

                                {/* Tasks Completed */}
                                <td className="py-3.5 px-2">
                                    <span className="text-sm font-medium text-text-ink">
                                        {row.completedTasks}
                                        <span className="text-text-muted">/{row.totalAssignedTasks}</span>
                                    </span>
                                </td>

                                {/* Progress Bar */}
                                <td className="py-3.5 px-2">
                                    <div className="flex items-center gap-3">
                                        <div className="flex-1 rounded-full h-1 overflow-hidden" style={{ backgroundColor: '#E8E4DF' }}>
                                            <div
                                                className="h-full rounded-full transition-all duration-500 ease-out"
                                                style={{
                                                    width: `${row.progress}%`,
                                                    backgroundColor: row.progress >= 100 ? '#6E7A5A' : row.progress > 0 ? '#7BBFAF' : 'transparent',
                                                }}
                                            />
                                        </div>
                                        <span className="text-[11px] font-bold text-text-muted w-8 text-right uppercase tracking-wider">{row.progress}%</span>
                                    </div>
                                </td>

                                {/* Last Active */}
                                <td className="py-3.5 px-2">
                                    <span className="text-sm text-text-muted font-medium">
                                        {row.lastActive ? formatTimeAgo(row.lastActive) : '—'}
                                    </span>
                                </td>

                                {/* Status Badge */}
                                <td className="py-3.5 px-2">
                                    <StatusBadge status={row.status} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Drag Handle Indicator */}
            <div className="flex justify-center pt-2 pb-1 cursor-ns-resize opacity-30 hover:opacity-60 transition-opacity">
                <div className="w-10 h-1 bg-neutral-300 rounded-full"></div>
            </div>
        </div>
    );
};

export default ParticipantProgressTable;
